import { gql, useQuery } from '@apollo/client';
import { VenueAvailabilities, VenueAvailabilitiesVariables } from './__queries__';
import { DateTime } from 'luxon';
import { useSelector } from '~Hooks';

const VENUE_QUERY = gql`
  query VenueAvailabilities($id: ID!) {
    venue: Venue(id: $id) {
      id
      name
      availabilities {
        id
        dayOfWeek
        timeRange {
          id
          start
          end
        }
      }
    }
  }
`;

enum DAY_OF_WEEK {
  MONDAY = 'MONDAY',
  TUESDAY = 'TUESDAY',
  WEDNESDAY = 'WEDNESDAY',
  THURSDAY = 'THURSDAY',
  FRIDAY = 'FRIDAY',
  SATURDAY = 'SATURDAY',
  SUNDAY = 'SUNDAY',
}

const FALLBACK_CLOSED = {
  isOpen: false,
  from: '--',
  to: '--',
};

type Availability = {
  id: string;
  dayOfWeek: DAY_OF_WEEK;
  timeRange: {
    id: string;
    start: string;
    end: string;
    initialStart?: string;
    initialEnd?: string;
    isMidnight?: boolean;
  };
};

const useOpeningHours = () => {
  const venueId = useSelector((state) => state.venueId);
  const now = DateTime.now();

  const venueQuery = useQuery<VenueAvailabilities, VenueAvailabilitiesVariables>(VENUE_QUERY, {
    fetchPolicy: 'cache-and-network',
    variables: {
      id: venueId ?? '',
    },
    skip: !venueId,
  });

  // The reason for this function is that it is difficult to check if a time is within timerange if the timerange exceeds midnight.
  // To solve this difficulty, the function splits the range // into new objects so that no individual range spans past midnight.
  // Example: start: 12:00 - 03:00 -> {start: 12:00, initialEnd: 03:00, end: 00:00}, {initialStart: 12:00, start: 00:00, end: 03:00}
  const splitOverMidnightAvailability = (availabilities: Availability[]) => {
    return availabilities.flatMap((availability) => {
      const startToUse = availability.timeRange.start;
      const endToUse = availability.timeRange.end;

      const start = DateTime.fromISO(startToUse);

      // If endTime is 00:00 we convert it to the last millisecond of the day for comparison
      const end =
        endToUse === '00:00' ? DateTime.fromISO('23:59:59:999') : DateTime.fromISO(endToUse);

      // If the end is 00:00 we don't need to split it into multiple parts
      if (endToUse === '00:00') {
        return [
          {
            ...availability,
            timeRange: {
              id: availability.timeRange.id,
              start: startToUse,
              end: endToUse,
            },
          } as Availability,
        ];
      }

      // If the availability does not span midnight, return it as is
      if (start < end) {
        return [
          {
            ...availability,
            timeRange: {
              id: availability.timeRange.id,
              start: startToUse,
              end: endToUse,
            },
          } as Availability,
        ];
      }

      // Split into two availabilities when spanning over midnight
      const nextDay = getNextDay(availability.dayOfWeek);

      return [
        {
          // First part: from start to 00:00 on the current day
          ...availability,
          timeRange: {
            id: availability.timeRange.id,
            start: startToUse,
            initialEnd: endToUse, // Initial end is saved for display purposes so we can display 12:00 - 03:00 instead of 12:00 - 00:00 and 00:00 - 03:00
            end: '00:00',
          },
        },
        {
          // Second part: from 00:00 to end on the next day
          ...availability,
          dayOfWeek: nextDay, // Change the day to the next day
          timeRange: {
            id: availability.timeRange.id,
            initialStart: startToUse, // Initial start is saved for display purposes so we can display 12:00 - 03:00 instead of 12:00 - 00:00 and 00:00 - 03:00
            start: '00:00',
            end: endToUse,
            isMidnight: true,
          },
        },
      ];
    });
  };

  // Helper function to get the next day of the week
  const getNextDay = (currentDay: DAY_OF_WEEK): DAY_OF_WEEK => {
    const daysOfWeek = Object.values(DAY_OF_WEEK);
    const currentIndex = daysOfWeek.indexOf(currentDay);
    if (currentIndex === -1) {
      throw new Error(`Invalid day of the week: ${currentDay}`);
    }

    const nextIndex = (currentIndex + 1) % daysOfWeek.length; // Wrap around if necessary (SUNDAY after SATURDAY)
    return daysOfWeek[nextIndex];
  };

  const availabilities = splitOverMidnightAvailability(
    venueQuery.data?.venue.availabilities ?? [],
  );

  const getCorrectAvailability = (availabilitiesList: Availability[], date: DateTime) => {
    for (const availability of availabilitiesList) {
      const { timeRange } = availability;

      // Check for 24h opening hours first
      if (timeRange.start === '00:00' && timeRange.end === '00:00') {
        return {
          isOpen: true,
          from: timeRange.start,
          to: timeRange.end,
        };
      }

      // Extracting time from the start and end strings
      const startTime = DateTime.fromISO(timeRange.start);
      // If endTime is 00:00 we convert it to the last second of the day for comparison
      const endTime =
        timeRange.end === '00:00' ? DateTime.fromISO('23:59:59') : DateTime.fromISO(timeRange.end);

      // Normalize the input date (just keep the time part for comparison, set year/month/day to 1970-01-01)
      const timeOnly = date.set({ year: 1970, month: 1, day: 1 });

      // Normalize start and end times for the same day (1970-01-01)
      const normalizedStartTime = startTime.set({ year: 1970, month: 1, day: 1 });
      const normalizedEndTime = endTime.set({ year: 1970, month: 1, day: 1 });

      if (normalizedStartTime < normalizedEndTime) {
        if (timeOnly >= normalizedStartTime && timeOnly <= normalizedEndTime) {
          return {
            isOpen: true,
            from: timeRange.initialStart ?? timeRange.start,
            to: timeRange.initialEnd ?? timeRange.end,
          };
        }
      }
    }
    return null;
  };

  const checkOpeningHoursForDate = (date?: Date) => {
    const timeForComparison = !date ? now : DateTime.fromJSDate(date);

    if (!timeForComparison || !timeForComparison.isValid) {
      return FALLBACK_CLOSED;
    }

    // Reason we use en-GB is because we save the day as english example WEDNESDAY
    const selectedDay = timeForComparison.setLocale('en-GB').weekdayLong.toUpperCase();
    const foundDayAvailabilities = availabilities.filter((a) => a.dayOfWeek === selectedDay);

    const availabilityResult = getCorrectAvailability(foundDayAvailabilities, timeForComparison);

    // If a matching availability was found, return it
    if (availabilityResult) {
      return availabilityResult;
    }

    // If the venue is closed at the selected time, display opening hours for the day
    // Filter out midnight availabilities and get unique time ranges
    const regularAvailabilities = foundDayAvailabilities.filter(
      (a) =>
        !a.timeRange.isMidnight && !(a.timeRange.start === '00:00' && a.timeRange.end === '00:00'),
    );

    if (regularAvailabilities.length === 0) {
      return {
        isOpen: false,
        from: '--',
        to: '--',
      };
    }

    // For multiple availability periods (e.g., 10-14, 16-22), show all periods. This values are not displayed in the UI, so these can be tweaked as needed in the future.
    if (regularAvailabilities.length > 1) {
      const timeRanges = regularAvailabilities
        .map((a) => {
          const from = a.timeRange.initialStart || a.timeRange.start;
          const to = a.timeRange.initialEnd || a.timeRange.end;
          return `${from}-${to}`;
        })
        .join(', ');

      return {
        isOpen: false,
        from: timeRanges,
        to: '', // Empty to indicate it's a combined display
      };
    }

    // Single availability period
    const availability = regularAvailabilities[0];
    return {
      isOpen: false,
      from: availability?.timeRange.initialStart || availability?.timeRange.start || '--',
      to: availability?.timeRange.initialEnd || availability?.timeRange.end || '--',
    };
  };

  return {
    today: checkOpeningHoursForDate(),
    loading: !venueQuery.data || venueQuery.loading,
    checkOpeningHoursForDate,
  };
};

export default useOpeningHours;
